import React, { useState, useEffect, useMemo } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { TableActions } from "../../../components/TableActions";
import { useWarehouseTaskItemStore } from "../../../store/warehouseTaskItemStore";
import { useWarehouseTaskStore } from "../../../store/warehouseTask";
import { WarehouseTaskItem, WarehouseTask } from "../../../types/warehouse";
import { DataTableColumn, Filter as FilterType } from "../../../types";
import { formatQuantity } from "../../../utils/format";
import { SmartNetworkImage } from "../../../components/SmartNetworkImage";
import { ImageLightbox } from "../../../components/ImageLightbox";
import { DateRangePicker } from "../../../components/DateRangePicker";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import {
  PackageCheck,
  UserCheck,
  Clock,
  MapPin,
  Eye,
  Printer,
  Boxes,
  Layers,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Package,
  Calendar,
  Warehouse,
  ShoppingBag,
  Activity,
  Image as ImageIcon,
  User,
} from "lucide-react";
import { WarehouseTaskDetailDialog } from "./WarehouseTaskDetailDialog";
import { WarehouseTaskPrintDialog } from "./WarehouseTaskPrintDialog";
import { toast } from "react-toastify";

const STATUS_MAP: Record<string, { label: string; bg: string; text: string }> = {
  PENDING_PICKING: { label: "Toplama Bekliyor", bg: "bg-orange-500/10 border-orange-500/20", text: "text-orange-600 dark:text-orange-400" },
  PICKING: { label: "Toplanıyor", bg: "bg-blue-500/10 border-blue-500/20", text: "text-blue-600 dark:text-blue-400" },
  PICKING_PAUSED: { label: "Toplama Duraklatıldı", bg: "bg-amber-500/10 border-amber-500/20", text: "text-amber-600 dark:text-amber-400" },
  PENDING_PACKAGING: { label: "Paketleme Bekliyor", bg: "bg-orange-500/10 border-orange-500/20", text: "text-orange-600 dark:text-orange-400" },
  PACKAGING: { label: "Paketleniyor", bg: "bg-purple-500/10 border-purple-500/20", text: "text-purple-600 dark:text-purple-400" },
  PACKAGING_PAUSED: { label: "Paketleme Duraklatıldı", bg: "bg-amber-500/10 border-amber-500/20", text: "text-amber-600 dark:text-amber-400" },
  COMPLETED: { label: "Tamamlandı", bg: "bg-emerald-500/10 border-emerald-500/20", text: "text-emerald-600 dark:text-emerald-400" },
  SHORTAGE: { label: "Eksik Bildirildi", bg: "bg-rose-500/10 border-rose-500/20", text: "text-rose-600 dark:text-rose-400" },
  CANCELLED: { label: "İptal Edildi", bg: "bg-slate-500/10 border-slate-500/20", text: "text-slate-600 dark:text-slate-400" },
};

export const WarehouseItemTrackingList: React.FC = () => {
  const itemStore = useWarehouseTaskItemStore();
  const taskStore = useWarehouseTaskStore();

  const apiBase = "https://api.plasiyo.com/";

  // Lightbox & Görsel Galerisi State'i
  const [galleryState, setGalleryState] = useState<{
    isOpen: boolean;
    images: string[];
    currentIndex: number;
    title?: string;
    sku?: string;
  }>({
    isOpen: false,
    images: [],
    currentIndex: 0,
  });

  // Task Dialog States
  const [selectedTask, setSelectedTask] = useState<WarehouseTask | null>(null);
  const [taskToPrint, setTaskToPrint] = useState<WarehouseTask | null>(null);
  const [printCategory, setPrintCategory] = useState<"PICKING" | "PACKING">("PICKING");

  // Filtre Seçenekleri
  const [staffOptions, setStaffOptions] = useState<{ label: string; value: number }[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<{ label: string; value: number }[]>([]);

  // Aktif Filtre State'leri
  const [filterPickingDates, setFilterPickingDates] = useState<(Date | null)[] | null>(null);
  const [filterPackagingDates, setFilterPackagingDates] = useState<(Date | null)[] | null>(null);
  const [selectedPickers, setSelectedPickers] = useState<number[]>([]);
  const [selectedPackers, setSelectedPackers] = useState<number[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(null);
  const [selectedProcessStatus, setSelectedProcessStatus] = useState<string>("ALL");
  const [selectedTaskStatuses, setSelectedTaskStatuses] = useState<string[]>([]);

  // Özet İstatistikler
  const [summary, setSummary] = useState<any>(null);

  const staffMap = useMemo(() => {
    const map = new Map<number, string>();
    staffOptions.forEach((s) => {
      map.set(s.value, s.label);
    });
    return map;
  }, [staffOptions]);

  const resolveItemUserName = (userId?: number | null, userObj?: { id?: number; name?: string } | null, item?: WarehouseTaskItem) => {
    if (userObj?.name && userObj.name.trim().length > 0) return userObj.name;
    if (!userId) return null;
    if (item?.task?.assignedUserId === userId && item.task.assignedUser?.name) return item.task.assignedUser.name;
    const action = (item?.task as any)?.actions?.find((a: any) => a.userId === userId || a.user?.id === userId);
    if (action?.user?.name) return action.user.name;
    if (staffMap.has(userId)) return staffMap.get(userId);
    return null;
  };

  // Veri yükleme (Seçenekler & KPI)
  useEffect(() => {
    const loadInitialOptions = async () => {
      const [staffList, warehouses,
        //summaryData
      ] = await Promise.all([
        itemStore.getStaffList(),
        itemStore.getWarehouseList(),
        //itemStore.getSummaryStats(),
      ]);
      setStaffOptions(staffList);
      setWarehouseOptions(warehouses);
      // if (summaryData) setSummary(summaryData);
    };
    loadInitialOptions();
  }, []);

  const refreshSummary = async () => {
    const summaryData = await itemStore.getSummaryStats();
    if (summaryData) setSummary(summaryData);
  };

  // Dinamik Filtre Yapısı
  const combinedFilters = useMemo<FilterType[]>(() => {
    const filters: FilterType[] = [];

    // 1. Toplama Tarih Aralığı
    if (filterPickingDates && filterPickingDates[0]) {
      const start = new Date(filterPickingDates[0]);
      start.setHours(0, 0, 0, 0);
      filters.push({
        field: "pickedAt",
        operator: "gte",
        value: start.toISOString(),
      });

      const end = filterPickingDates[1] ? new Date(filterPickingDates[1]) : new Date(filterPickingDates[0]);
      end.setHours(23, 59, 59, 999);
      filters.push({
        field: "pickedAt",
        operator: "lte",
        value: end.toISOString(),
      });
    }

    // 2. Paketleme Tarih Aralığı
    if (filterPackagingDates && filterPackagingDates[0]) {
      const start = new Date(filterPackagingDates[0]);
      start.setHours(0, 0, 0, 0);
      filters.push({
        field: "packedAt",
        operator: "gte",
        value: start.toISOString(),
      });

      const end = filterPackagingDates[1] ? new Date(filterPackagingDates[1]) : new Date(filterPackagingDates[0]);
      end.setHours(23, 59, 59, 999);
      filters.push({
        field: "packedAt",
        operator: "lte",
        value: end.toISOString(),
      });
    }

    // 3. Toplayan Personel
    if (selectedPickers.length > 0) {
      filters.push({
        field: "pickedById",
        operator: "in",
        value: selectedPickers,
      });
    }

    // 4. Paketleyen Personel
    if (selectedPackers.length > 0) {
      filters.push({
        field: "packedById",
        operator: "in",
        value: selectedPackers,
      });
    }

    // 5. Depo Filtresi
    if (selectedWarehouse) {
      filters.push({
        field: "task.order.warehouseId",
        operator: "eq",
        value: selectedWarehouse,
      });
    }

    // 6. Görev Statüsü
    if (selectedTaskStatuses.length > 0) {
      filters.push({
        field: "task.status",
        operator: "in",
        value: selectedTaskStatuses,
      });
    }

    // 7. İşlem Durumu
    if (selectedProcessStatus === "PICKED") {
      filters.push({
        field: "collectedQty",
        operator: "gt",
        value: 0,
      });
    } else if (selectedProcessStatus === "PACKED") {
      filters.push({
        field: "packedQty",
        operator: "gt",
        value: 0,
      });
    } else if (selectedProcessStatus === "COMPLETED") {
      filters.push(
        { field: "collectedQty", operator: "gt", value: 0 },
        { field: "packedQty", operator: "gt", value: 0 }
      );
    } else if (selectedProcessStatus === "PENDING") {
      filters.push({
        field: "collectedQty",
        operator: "eq",
        value: 0,
      });
    }

    return filters;
  }, [
    filterPickingDates,
    filterPackagingDates,
    selectedPickers,
    selectedPackers,
    selectedWarehouse,
    selectedTaskStatuses,
    selectedProcessStatus,
  ]);

  const handleResetFilters = () => {
    setFilterPickingDates(null);
    setFilterPackagingDates(null);
    setSelectedPickers([]);
    setSelectedPackers([]);
    setSelectedWarehouse(null);
    setSelectedProcessStatus("ALL");
    setSelectedTaskStatuses([]);
  };

  const handleOpenTaskDetail = async (item: WarehouseTaskItem) => {
    if (item.task?.id || item.taskId) {
      const taskId = item.task?.id || item.taskId;
      try {
        const fullTask = await taskStore.getSingleTask(taskId, true);
        if (fullTask) {
          setSelectedTask(fullTask);
        } else {
          setSelectedTask(item.task || ({ id: taskId } as any));
        }
      } catch {
        setSelectedTask(item.task || ({ id: taskId } as any));
      }
    }
  };

  const handlePrintTask = async (item: WarehouseTaskItem, category: "PICKING" | "PACKING") => {
    const taskId = item.task?.id || item.taskId;
    if (taskId) {
      try {
        const fullTask = await taskStore.getSingleTask(taskId, true);
        setTaskToPrint(fullTask || item.task || ({ id: taskId } as any));
        setPrintCategory(category);
      } catch {
        setTaskToPrint(item.task || ({ id: taskId } as any));
        setPrintCategory(category);
      }
    }
  };

  // Kolon Tanımları
  const columns = useMemo<DataTableColumn<WarehouseTaskItem>[]>(() => [
    {
      field: "stock.images",
      header: "Görsel",
      width: "80px",
      align: "center",
      body: (r) => {
        const imageUrls = (r.stock?.images || [])
          .filter((img: any) => img?.url && typeof img.url === "string" && img.url.trim().length > 0)
          .map((img: any) => `${apiBase}${img.url}`);
        const hasImages = imageUrls.length > 0;

        return (
          <div
            onClick={() => {
              if (hasImages) {
                setGalleryState({
                  isOpen: true,
                  images: imageUrls,
                  currentIndex: 0,
                  title: r.stock?.name,
                  sku: r.stock?.sku,
                });
              }
            }}
            className={`relative group w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center border border-borderColor bg-card shadow-xs transition-all duration-200 ${hasImages ? "cursor-pointer hover:border-primary hover:shadow-md hover:scale-105" : "opacity-60"
              }`}
            title={hasImages ? "Görselleri büyütmek için tıklayın" : "Görsel yok"}
          >
            <SmartNetworkImage
              imageUrls={imageUrls}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
            {hasImages && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white backdrop-blur-[1px]">
                <Eye size={16} className="transform group-hover:scale-110 transition-transform drop-shadow" />
              </div>
            )}
            {imageUrls.length > 1 && (
              <div className="absolute bottom-0.5 right-0.5 px-1 py-0.2 rounded bg-black/75 text-white text-[8px] font-black tracking-tighter flex items-center gap-0.5 shadow-xs">
                <ImageIcon size={8} /> {imageUrls.length}
              </div>
            )}
          </div>
        );
      },
    },
    {
      field: "stock.name",
      header: "Ürün & Varyant",
      width: "280px",
      sortable: true,
      body: (r) => {
        const hasComponents = r.stock?.components && r.stock.components.length > 0;
        return (
          <div className="flex flex-col gap-1 py-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-mono text-[10px] font-black tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20">
                {r.stock?.sku || "SKU YOK"}
              </span>
              {r.variant?.name && (
                <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 dark:text-indigo-400 px-1.5 py-0.5 rounded border border-indigo-200 dark:border-indigo-800">
                  {r.variant.name}
                </span>
              )}
              {hasComponents && r.stock?.components && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-purple-500/10 text-purple-600 border border-purple-500/20">
                  <Boxes size={10} /> Set ({r.stock.components.length})
                </span>
              )}
            </div>

            <span className="text-xs font-bold text-text line-clamp-2 leading-snug">
              {r.stock?.name}
            </span>

            {/* Raf Konumu */}
            {(r.stock?.shelfLocation || r.locationSnapshot) && (
              <div className="flex items-center gap-1 text-[9px] text-text/60 mt-0.5">
                <MapPin size={10} className="text-primary shrink-0" />
                <span className="font-bold uppercase tracking-tight">
                  {r.stock?.shelfLocation || r.locationSnapshot}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      field: "task.order.orderNumber",
      header: "Sipariş & Görev",
      width: "210px",
      sortable: true,
      body: (r) => {
        const order = r.task?.order;
        return (
          <div className="flex flex-col gap-1 py-1">
            <div className="flex items-center gap-1.5">
              <span
                onClick={() => handleOpenTaskDetail(r)}
                className="font-mono text-xs font-black text-blue-600 dark:text-blue-400 hover:underline cursor-pointer flex items-center gap-1"
                title="Görevi ve Siparişi Aç"
              >
                <ShoppingBag size={12} /> {order?.orderNumber || `#${r.taskId}`}
              </span>
              {order?.source && (
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.2 rounded bg-bgAlt border border-borderColor opacity-80">
                  {order.source}
                </span>
              )}
            </div>

            {order?.account?.name && (
              <span className="text-[11px] font-medium text-text/80 truncate" title={order.account.name}>
                {order.account.name}
              </span>
            )}

            <div className="flex items-center gap-2 text-[9px] text-text/50">
              <span className="flex items-center gap-1 font-medium">
                <Warehouse size={10} /> {order?.warehouse?.name || "Ana Depo"}
              </span>
              <span>• Görev #{r.taskId}</span>
            </div>
          </div>
        );
      },
    },
    {
      field: "quantity",
      header: "Miktar Durumu",
      width: "160px",
      align: "center",
      body: (r) => {
        const isPickComplete = (r.collectedQty || 0) >= r.quantity;
        const isPackComplete = (r.packedQty || 0) >= r.quantity;

        return (
          <div className="flex flex-col gap-1 py-1 w-full max-w-[150px] mx-auto">
            <div className="flex items-center justify-between text-[11px] px-2 py-0.5 rounded bg-bgAlt border border-borderColor">
              <span className="font-bold opacity-60 text-[9px] uppercase">Talep</span>
              <span className="font-black text-text">
                {formatQuantity(r.quantity)} {r.stock?.unit?.name || "ADET"}
              </span>
            </div>

            <div className="flex items-center justify-between text-[11px] px-2 py-0.5 rounded bg-blue-500/5 border border-blue-500/20">
              <span className="font-bold text-blue-600 dark:text-blue-400 text-[9px] uppercase">Toplanan</span>
              <span className={`font-black ${isPickComplete ? "text-success" : "text-blue-600 dark:text-blue-400"}`}>
                {formatQuantity(r.collectedQty || 0)} / {formatQuantity(r.quantity)}
              </span>
            </div>

            <div className="flex items-center justify-between text-[11px] px-2 py-0.5 rounded bg-purple-500/5 border border-purple-500/20">
              <span className="font-bold text-purple-600 dark:text-purple-400 text-[9px] uppercase">Paketlenen</span>
              <span className={`font-black ${isPackComplete ? "text-success" : "text-purple-600 dark:text-purple-400"}`}>
                {formatQuantity(r.packedQty || 0)} / {formatQuantity(r.quantity)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      field: "pickedBy.name",
      header: "Toplama Bilgisi",
      width: "210px",
      sortable: true,
      body: (r) => {
        const isPicked = (r.collectedQty || 0) > 0 || !!r.pickedAt || !!r.pickedById || !!r.pickedBy;

        if (!isPicked) {
          return (
            <div className="flex items-center gap-1 text-[11px] text-text/40 italic py-1">
              <Clock size={12} className="opacity-50" />
              <span>Henüz Toplanmadı</span>
            </div>
          );
        }

        const pickerName = resolveItemUserName(r.pickedById, r.pickedBy, r) || "Personel";

        return (
          <div className="flex flex-col gap-1 py-1">
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-700 dark:text-blue-300">
              <UserCheck size={13} className="text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="font-bold text-xs truncate">
                {pickerName}
              </span>
            </div>

            {r.pickedAt ? (
              <div className="flex items-center gap-1 text-[10px] text-text/70 pl-1 font-mono">
                <Calendar size={11} className="text-blue-500 shrink-0" />
                <span>
                  {new Date(r.pickedAt).toLocaleDateString("tr-TR")}{" "}
                  {new Date(r.pickedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ) : (
              <span className="text-[9px] opacity-40 italic pl-1">Zaman kaydı yok</span>
            )}
          </div>
        );
      },
    },
    {
      field: "packedBy.name",
      header: "Paketleme Bilgisi",
      width: "210px",
      sortable: true,
      body: (r) => {
        const isPacked = (r.packedQty || 0) > 0 || !!r.packedAt || !!r.packedById || !!r.packedBy;

        if (!isPacked) {
          return (
            <div className="flex items-center gap-1 text-[11px] text-text/40 italic py-1">
              <Clock size={12} className="opacity-50" />
              <span>Henüz Paketlenmedi</span>
            </div>
          );
        }

        const packerName = resolveItemUserName(r.packedById, r.packedBy, r) || "Personel";

        return (
          <div className="flex flex-col gap-1 py-1">
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/20 text-purple-700 dark:text-purple-300">
              <PackageCheck size={13} className="text-purple-600 dark:text-purple-400 shrink-0" />
              <span className="font-bold text-xs truncate">
                {packerName}
              </span>
            </div>

            {r.packedAt ? (
              <div className="flex items-center gap-1 text-[10px] text-text/70 pl-1 font-mono">
                <Calendar size={11} className="text-purple-500 shrink-0" />
                <span>
                  {new Date(r.packedAt).toLocaleDateString("tr-TR")}{" "}
                  {new Date(r.packedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ) : (
              <span className="text-[9px] opacity-40 italic pl-1">Zaman kaydı yok</span>
            )}
          </div>
        );
      },
    },
    {
      field: "task.status",
      header: "Görev Durumu",
      width: "150px",
      align: "center",
      sortable: true,
      body: (r) => {
        const statusKey = r.task?.status || "PENDING_PICKING";
        const cfg = STATUS_MAP[statusKey] || {
          label: statusKey,
          bg: "bg-slate-500/10 border-slate-500/20",
          text: "text-slate-600 dark:text-slate-400",
        };

        return (
          <span className={`inline-block px-2.5 py-1 rounded-lg border text-[11px] font-black uppercase tracking-tight shadow-xs ${cfg.bg} ${cfg.text}`}>
            {cfg.label}
          </span>
        );
      },
    },
    {
      field: "actions",
      header: "İşlemler",
      width: "110px",
      align: "center",
      body: (r) => (
        <TableActions
          actions={[
            {
              icon: Eye,
              title: "Görev Detayını Aç",
              colorClass: "text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30",
              onClick: (e) => {
                e.stopPropagation();
                handleOpenTaskDetail(r);
              },
            },
            {
              icon: Printer,
              title: "Etiket Yazdır",
              colorClass: "text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30",
              onClick: (e) => {
                e.stopPropagation();
                handlePrintTask(r, "PICKING");
              },
            },
          ]}
        />
      ),
    },
  ], [apiBase]);

  // Filtre Barı Bileşeni
  const filterBar = (
    <div className="flex flex-col gap-3 p-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 items-end">
        {/* 1. Toplama Tarih Aralığı */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold text-text/70 uppercase tracking-tight flex items-center gap-1">
            <UserCheck size={13} className="text-blue-500" /> Toplama Tarihi
          </label>
          <DateRangePicker
            value={filterPickingDates}
            onChange={setFilterPickingDates}
          />
        </div>

        {/* 2. Paketleme Tarih Aralığı */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold text-text/70 uppercase tracking-tight flex items-center gap-1">
            <PackageCheck size={13} className="text-purple-500" /> Paketleme Tarihi
          </label>
          <DateRangePicker
            value={filterPackagingDates}
            onChange={setFilterPackagingDates}
          />
        </div>

        {/* 3. Toplayan Personel */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold text-text/70 uppercase tracking-tight flex items-center gap-1">
            <User size={13} className="text-blue-500" /> Toplayan Personel
          </label>
          <MultiSelect
            value={selectedPickers}
            options={staffOptions}
            onChange={(e) => setSelectedPickers(e.value)}
            placeholder="Tüm Toplayıcılar"
            className="w-full"
            display="chip"
            filter
          />
        </div>

        {/* 4. Paketleyen Personel */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold text-text/70 uppercase tracking-tight flex items-center gap-1">
            <User size={13} className="text-purple-500" /> Paketleyen Personel
          </label>
          <MultiSelect
            value={selectedPackers}
            options={staffOptions}
            onChange={(e) => setSelectedPackers(e.value)}
            placeholder="Tüm Paketleyiciler"
            className="w-full"
            display="chip"
            filter
          />
        </div>

        {/* 5. İşlem Durumu */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold text-text/70 uppercase tracking-tight">
            İşlem Durumu
          </label>
          <Dropdown
            value={selectedProcessStatus}
            options={[
              { label: "Tümü", value: "ALL" },
              { label: "Toplanan Ürünler", value: "PICKED" },
              { label: "Paketlenen Ürünler", value: "PACKED" },
              { label: "Tamamen Tamamlananlar", value: "COMPLETED" },
              { label: "Henüz Toplanmayanlar", value: "PENDING" },
            ]}
            onChange={(e) => setSelectedProcessStatus(e.value)}
            className="w-full"
          />
        </div>

        {/* 6. Depo Seçimi & Filtre Temizle */}
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-[11px] font-bold text-text/70 uppercase tracking-tight">
              Depo
            </label>
            <Dropdown
              value={selectedWarehouse}
              options={warehouseOptions}
              onChange={(e) => setSelectedWarehouse(e.value)}
              placeholder="Tüm Depolar"
              showClear
              className="w-full"
            />
          </div>

          <button
            onClick={handleResetFilters}
            className="p-2.5 rounded-xl border border-borderColor hover:bg-bgAlt text-text/70 hover:text-danger transition-colors flex items-center justify-center self-end shadow-xs cursor-pointer"
            title="Filtreleri Sıfırla"
          >
            <RotateCcw size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 h-full ">
      {/* KPI Kartları
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="card p-3 rounded-2xl border border-borderColor bg-card shadow-xs flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-[10px] font-black uppercase tracking-wider text-text/60">
                Toplam İşlenen Kalem
              </span>
              <span className="text-xl font-black text-text mt-0.5">
                {formatQuantity(summary.totalItems || 0)}
              </span>
            </div>
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <Activity size={20} />
            </div>
          </div>

          <div className="card p-3 rounded-2xl border border-borderColor bg-card shadow-xs flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-[10px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Toplanan Kalemler
              </span>
              <span className="text-xl font-black text-blue-600 dark:text-blue-400 mt-0.5">
                {formatQuantity(summary.pickedCount || 0)}
              </span>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 border border-blue-500/20 flex items-center justify-center">
              <UserCheck size={20} />
            </div>
          </div>

          <div className="card p-3 rounded-2xl border border-borderColor bg-card shadow-xs flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-[10px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400">
                Paketlenen Kalemler
              </span>
              <span className="text-xl font-black text-purple-600 dark:text-purple-400 mt-0.5">
                {formatQuantity(summary.packedCount || 0)}
              </span>
            </div>
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 border border-purple-500/20 flex items-center justify-center">
              <PackageCheck size={20} />
            </div>
          </div>

          <div className="card p-3 rounded-2xl border border-borderColor bg-card shadow-xs flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Tamamlanan Kalemler
              </span>
              <span className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
                {formatQuantity(summary.fullyCompletedCount || 0)}
              </span>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 size={20} />
            </div>
          </div>
        </div>
      )}
 */}
      {/* Ana Tablo */}
      <div className="card p-0 shadow-sm border border-borderColor rounded-2xl overflow-hidden flex-1 flex flex-col">
        <EDataTable<WarehouseTaskItem>
          tableKey="warehouse_task_items_tracking"
          placeholder="Ürün adı, SKU, barkod, sipariş no veya müşteri ara..."
          searchFields={[
            { field: "stock.name", operator: "contains" },
            { field: "stock.sku", operator: "contains" },
            { field: "task.order.orderNumber", operator: "contains" },
            { field: "task.order.account.name", operator: "contains" },
          ]}
          useStore={() => itemStore}
          dataKey="id"
          title="Depo Ürün Hareketleri & Takip"
          subTitle="Depo görev kalemlerinin toplanma, paketlenme, personel ve zaman detaylarını filtreleyin."
          filters={combinedFilters}
          filterBar={filterBar}
          defaultFilterExpanded={false}
          columns={columns}
          scrollh={420}
        />
      </div>

      {/* Görsel Lightbox Modal */}
      <ImageLightbox
        isOpen={galleryState.isOpen}
        onClose={() => setGalleryState((prev) => ({ ...prev, isOpen: false }))}
        images={galleryState.images}
        currentIndex={galleryState.currentIndex}
        onIndexChange={(idx) => setGalleryState((prev) => ({ ...prev, currentIndex: idx }))}
        title={galleryState.title}
        sku={galleryState.sku}
      />

      {/* Görev Detay Dialogu */}
      {selectedTask && (
        <WarehouseTaskDetailDialog
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          task={selectedTask}
        />
      )}

      {/* Görev Yazdırma Dialogu */}
      {taskToPrint && (
        <WarehouseTaskPrintDialog
          isOpen={!!taskToPrint}
          onClose={() => setTaskToPrint(null)}
          task={taskToPrint}
          printCategory={printCategory}
        />
      )}
    </div>
  );
};
